// client/src/App.js 

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Loader } from 'lucide-react';
import PropTypes from 'prop-types';
import { hasPermission, clearPermissionCache } from './utils/permissions';
import { 
  api
} from './utils/apiClient';
import websocketClient from './utils/websocketClient';
import { theme } from './styles/app/theme';
import { NotificationProvider } from './services/NotificationContext';
import { ActivityTrackingProvider } from './components/app/context/ActivityTrackingContext';
import IdleTimeoutWarning from './components/app/common/IdleTimeoutWarning';
import './App.css';

// Lazy load all components (complete original set)
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const Login = lazy(() => import('./components/app/Login'));
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ './components/app/DashboardContainer'));
const Navbar = lazy(() => import(/* webpackPrefetch: true */ './components/app/Navbar'));
const Profile = lazy(() => import('./components/app/Profile'));
const UserManagement = lazy(() => import('./components/app/UserManagement'));
// REMOVED: MachineManagement - route redirects to /assets/machines, sub-components still used by MachinesTab
// REMOVED: PartManagement - route redirects to /assets/parts, replaced by PartsTab
const AIChat = lazy(() => import('./components/app/AIChat'));
const KaizenForm = lazy(() => import('./components/app/KaizenManagement/KaizenForm'));
const KaizenList = lazy(() => import('./components/app/KaizenManagement/KaizenList'));
const KaizenLeaderboard = lazy(() => import('./components/app/KaizenManagement/KaizenLeaderboard'));
const AreaList = lazy(() => import('./components/app/SixS/AreaList'));
const AuditForm = lazy(() => import('./components/app/SixS/AuditForm'));
const Analysis = lazy(() => import('./components/app/SixS/Analysis'));
const SixSManagement = lazy(() => import('./components/app/SixS/SixSManagement'));
// REMOVED: AnalyticsDashboard - orphaned route, domain-specific analytics used instead (PM, Quality, 6S)
const ShiftReport = lazy(() => import('./components/app/ShiftReport'));
const Timeline = lazy(() => import('./components/app/Timeline'));
const SafetyIncidentList = lazy(() => import('./components/app/Safety/SafetyIncidentList'));
const SafetyIncidentDetails = lazy(() => import('./components/app/Safety/SafetyIncidentDetails'));
const IncidentForm = lazy(() => import('./components/app/Safety/IncidentForm'));
const SafetyDashboard = lazy(() => import('./components/app/Safety/SafetyDashboard'));
const PMDashboard = lazy(() => import('./components/app/PreventiveMaintenance/PMDashboard'));
const PMAreaList = lazy(() => import('./components/app/PreventiveMaintenance/PMAreaList'));
const PMAreaForm = lazy(() => import('./components/app/PreventiveMaintenance/PMAreaForm'));
const PMTaskExecution = lazy(() => import('./components/app/PreventiveMaintenance/PMTaskExecution'));
const PMAnalytics = lazy(() => import('./components/app/PreventiveMaintenance/PMAnalytics'));
const ResolutionManagement = lazy(() => import('./components/app/ResolutionManagement'));
const DiagnosticTreeManagement = lazy(() => import('./components/app/DiagnosticTrees'));
const ChangeoverDashboard = lazy(() => import('./components/app/SMED/ChangeoverDashboard'));
const ScheduleChangeover = lazy(() => import('./components/app/SMED/ScheduleChangeover'));
const ChangeoverList = lazy(() => import('./components/app/SMED/ChangeoverList'));
const ChecklistTemplateBuilder = lazy(() => import('./components/app/SMED/Templates/ChecklistTemplateBuilder'));
const ChecklistTemplateList = lazy(() => import('./components/app/SMED/Templates/ChecklistTemplateList'));
const RBACManagement = lazy(() => import('./components/app/RBAC/RBACManagement'));
const LeaveManagement = lazy(() => import('./components/app/LeaveManagement'));
// REMOVED: PersonalLeaveView - consolidated into LeaveCalendar peer view
const ParameterManagement = lazy(() => import('./components/app/MachineParameters/ParameterManagement'));
const AssetManagement = lazy(() => import('./components/app/AssetManagement'));
const AssetTree = lazy(() => import('./components/app/AssetTree'));
const DocumentControl = lazy(() => import('./components/app/DocumentControl'));
const PurchaseRequestManagement = lazy(() => import('./components/app/PurchaseRequestManagement'));
const QualityDashboard = lazy(() => import('./components/app/QualityManagement/QualityDashboard'));
const QualityAnalytics = lazy(() => import('./components/app/QualityManagement/QualityAnalytics'));

// HR Management
const HRSettings = lazy(() => import('./components/app/HRManagement/HRSettings'));
const HREmployeeProfiles = lazy(() => import('./components/app/HRManagement/HREmployeeProfiles'));
const HRTeamHierarchy = lazy(() => import('./components/app/HRManagement/HRTeamHierarchy'));

// Help Center
const HelpCenter = lazy(() => import('./components/app/Help'));
const HelpArticle = lazy(() => import('./components/app/Help/HelpArticle'));
const HelpManagement = lazy(() => import('./components/app/Help/HelpManagement'));
const HelpRequestReasonSettings = lazy(() => import('./components/app/Help/HelpRequestReasonSettings'));

// Training Management
const TrainingDashboard = lazy(() => import('./components/app/TrainingManagement/TrainingDashboard'));
const TrainingManagementPage = lazy(() => import('./components/app/TrainingManagement'));
const TrainingBuilder = lazy(() => import('./components/app/TrainingManagement/TrainingBuilder'));
const TrainingDetails = lazy(() => import('./components/app/TrainingManagement/TrainingDetails'));

// User Performance Stats
const UserStats = lazy(() => import('./components/app/UserStats'));

// PLC Monitoring
const PLCManagement = lazy(() => import('./components/app/PLCMonitoring/PLCManagement'));

// Compliance Reporting (FDA 21 CFR Part 11)
const ComplianceReporting = lazy(() => import('./components/app/ComplianceReporting'));

// Wrapper components for routes with params
const PMAreaEditWrapper = () => {
  const { id } = useParams();
  return <PMAreaForm areaId={id} />;
};

PMAreaEditWrapper.propTypes = {};

// Wrapper for PM Task Execution route
const PMTaskExecutionWrapper = () => {
  const { areaId } = useParams();
  return <PMTaskExecution areaId={areaId} />;
};

PMTaskExecutionWrapper.propTypes = {};

// Wrapper for Training Details route
const TrainingDetailsWrapper = ({ user }) => {
  const { assignmentId } = useParams();
  return <TrainingDetails assignmentId={assignmentId} user={user} />;
};

TrainingDetailsWrapper.propTypes = {
  user: PropTypes.object
};

// Wrapper for Training Builder edit route
const TrainingBuilderWrapper = ({ user }) => {
  const { id } = useParams();
  return <TrainingBuilder trainingId={id} user={user} />;
};

TrainingBuilderWrapper.propTypes = {
  user: PropTypes.object
};

// Enhanced loading spinner
const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center space-y-4">
      <Loader className="animate-spin h-8 w-8 text-blue-600" />
      <span className="text-gray-600">{message}</span>
    </div>
  </div>
);

LoadingSpinner.propTypes = {
  message: PropTypes.string
};

// ============= PRODUCTION READY USER OBJECT PROCESSING =============

/**
 * Process user object from server - PRODUCTION VERSION
 * Handles both string and object role formats from server
 * @param {object} serverUser - User object from server
 * @returns {object|null} Processed user object or null
 */
const processUserObject = (serverUser) => {
  if (!serverUser) return null;

  // Handle role information with robust type checking
  let primaryRole = 'No Role';
  let additionalRoles = [];
  let permissions = [];

  // Handle primaryRole (can be string or object)
  if (serverUser.primaryRole) {
    primaryRole = typeof serverUser.primaryRole === 'object' 
      ? (serverUser.primaryRole.name || serverUser.primaryRole.key || 'Unknown Role')
      : serverUser.primaryRole;
  } else if (serverUser.role) {
    primaryRole = typeof serverUser.role === 'object'
      ? (serverUser.role.name || serverUser.role.key || 'Unknown Role') 
      : serverUser.role;
  }

  // Handle additionalRoles (array of objects or strings)
  if (Array.isArray(serverUser.additionalRoles)) {
    additionalRoles = serverUser.additionalRoles.map(role => {
      if (typeof role === 'object') {
        return {
          id: role.id || role._id,
          name: role.name || role.key || 'Unknown Role',
          key: role.key,
        };
      }
      return { name: role };
    });
  }

  // Handle permissions (flat array from server)
  if (Array.isArray(serverUser.permissions)) {
    permissions = serverUser.permissions;
  }

  // Determine role status
  const roleStatus = (primaryRole && primaryRole !== 'No Role') ? 'assigned' : 'unassigned';

  const processedUser = {
    // Core user data
    id: serverUser.id || serverUser._id,
    name: serverUser.name,
    email: serverUser.email,
    isVerified: serverUser.isVerified,
    isActive: serverUser.isActive,
    
    // Role information (normalized)
    primaryRole,
    additionalRoles,
    permissions,
    roleStatus,
    
    // Legacy compatibility
    role: primaryRole,
    
    // Account metadata
    createdAt: serverUser.createdAt,
    updatedAt: serverUser.updatedAt,
    lastLoginAt: serverUser.lastLoginAt,
    loginCount: serverUser.loginCount || 0,
    passwordChangedAt: serverUser.passwordChangedAt || null,
    twoFactorEnabled: Boolean(serverUser.twoFactorEnabled)
  };

  return processedUser;
};

// ============= PRODUCTION READY PROTECTED ROUTE COMPONENT =============

const ProtectedRoute = ({
  user,
  permission,
  permissions = [],
  requireAll = false,
  children,
  fallback = <Navigate to="/unauthorized" replace />
}) => {
  const location = useLocation();

  // No user = redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if expired (annual policy) or never set
  if (location.pathname !== '/profile') {
    const passwordChangedAt = user.passwordChangedAt;
    const needsChange = !passwordChangedAt ||
      (new Date() - new Date(passwordChangedAt)) / (1000 * 60 * 60 * 24) >= 365;
    if (needsChange) {
      return <Navigate to="/profile" replace />;
    }
  }

  // Use permission checking with enhanced error handling
  let hasAccess = true;

  try {
    if (permission) {
      // Single permission check
      hasAccess = hasPermission(user, permission);
    } else if (permissions.length > 0) {
      // Multiple permissions check
      if (requireAll) {
        hasAccess = permissions.every(p => hasPermission(user, p));
      } else {
        hasAccess = permissions.some(p => hasPermission(user, p));
      }
    }
  } catch (error) {
    hasAccess = false;
  }

  if (!hasAccess) {
    return fallback;
  }

  return children;
};

ProtectedRoute.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    permissions: PropTypes.array
  }),
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node
};

// ============= LAYOUT RESET WRAPPER =============

const LayoutResetWrapper = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Force layout recalculation on route change
    const forceReflow = () => {
      // Force DOM reflow by reading layout properties
      document.body.offsetHeight; // eslint-disable-line no-unused-expressions
      
      // Reset any sticky layout states
      requestAnimationFrame(() => {
        // Trigger resize event to force layout recalculation in charts/components
        window.dispatchEvent(new Event('resize'));
      });
    };
    
    // Small delay to ensure the new component has mounted
    const timeoutId = setTimeout(forceReflow, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
  
  return children;
};

LayoutResetWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

// ============= AUTHENTICATED LAYOUT =============

const AuthenticatedLayout = ({ user, setUser, handleLogout, children }) => {
  return (
    <>
      <Suspense fallback={<LoadingSpinner message="Loading navigation..." />}>
        <Navbar user={user} setUser={setUser} logout={handleLogout} />
      </Suspense>
      <LayoutResetWrapper>
        {children}
      </LayoutResetWrapper>
    </>
  );
};

AuthenticatedLayout.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    permissions: PropTypes.array,
    primaryRole: PropTypes.string,
    role: PropTypes.string
  }),
  setUser: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

// ============= PRODUCTION ERROR BOUNDARY =============

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    clearPermissionCache();
    
    // Report to error service in production
    if (process.env.NODE_ENV === 'production' && window.reportError) {
      window.reportError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              The application encountered an unexpected error.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Refresh Page
              </button>
              <button 
                onClick={() => {
                  clearPermissionCache();
                  window.location.href = '/login';
                }}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

// ============= MAIN APP COMPONENT =============

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authRetryCount, setAuthRetryCount] = useState(0);
  const [authRetryCountdown, setAuthRetryCountdown] = useState(0);

  // ============= USER MANAGEMENT =============

  const updateUser = useCallback((userData) => {
    if (!userData) {
      setUser(null);
      return;
    }

    try {
      const processedUser = processUserObject(userData);
      setUser(processedUser);
      
      // Clear permission cache for new user
      clearPermissionCache();
    } catch (error) {
      setUser(null);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      // Clear permission cache
      clearPermissionCache();
      
      // Clear user state
      setUser(null);
      
      // Call logout API to clear cookies
      await api.auth.logout();
      
      // Redirect to login
      window.location.href = '/login';
      
    } catch (error) {
      // Force logout even if API call fails
      setUser(null);
      window.location.href = '/login';
    }
  }, []);

  // ============= AUTHENTICATION INITIALIZATION =============

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        setAuthError(null);

        // Check if we have any auth cookies by trying to fetch user
        const response = await api.auth.me();
        
        if (response?.data?.user) {
          updateUser(response.data.user);
        } else {
          handleLogout();
        }

      } catch (error) {
        // 401 errors are expected when not logged in - not an error condition
        if (error.status === 401) {
          setUser(null);
        } else {
          const isServerError = !error.status || [502, 503, 504].includes(error.status);
          setAuthError({
            message: isServerError
              ? 'Unable to reach the server. It may be temporarily unavailable.'
              : error.message,
            type: isServerError ? 'server' : 'auth',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [updateUser, handleLogout, authRetryCount]);

  // ============= WEBSOCKET CONNECTION =============
  
  useEffect(() => {
    if (user) {
      // Connect WebSocket when user is authenticated
      websocketClient.connect(user);
    } else {
      // Disconnect when user logs out
      websocketClient.disconnect();
    }

    // Cleanup on unmount
    return () => {
      websocketClient.disconnect();
    };
  }, [user]);

  // ============= AUTO-RETRY FOR SERVER ERRORS =============

  useEffect(() => {
    if (!authError || authError.type !== 'server') return;

    // Auto-retry up to 3 times with increasing delay (10s, 20s, 30s)
    const maxAutoRetries = 3;
    if (authRetryCount >= maxAutoRetries) return;

    const delay = (authRetryCount + 1) * 10;
    setAuthRetryCountdown(delay);

    const countdownInterval = setInterval(() => {
      setAuthRetryCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setAuthRetryCount(c => c + 1);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [authError, authRetryCount]);

  // ============= LOADING STATE =============

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <LoadingSpinner message="Initializing application..." />
      </ThemeProvider>
    );
  }

  // ============= ERROR STATE =============

  if (authError && !user) {
    const isServer = authError.type === 'server';

    return (
      <ThemeProvider theme={theme}>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h1 className="text-2xl font-bold mb-4" style={{ color: isServer ? '#d97706' : '#dc2626' }}>
              {isServer ? 'Connection Issue' : 'Authentication Error'}
            </h1>
            <p className="text-gray-600 mb-2">{authError.message}</p>
            {isServer && (
              <p className="text-gray-400 text-sm mb-6">
                {authRetryCountdown > 0
                  ? `Retrying automatically in ${authRetryCountdown}s...`
                  : authRetryCount >= 3
                    ? 'Automatic retries exhausted. Please try manually.'
                    : 'Retrying...'}
              </p>
            )}
            {!isServer && <div className="mb-6" />}
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {isServer ? 'Retry Now' : 'Try Again'}
            </button>
            {!isServer && (
              <button
                onClick={() => { window.location.href = '/login'; }}
                className="ml-3 text-blue-600 hover:text-blue-800 underline"
              >
                Go to Login
              </button>
            )}
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // ============= MAIN APP RENDER =============

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <NotificationProvider>
            <ActivityTrackingProvider isAuthenticated={!!user} isDisplayAccount={!!user?.isDisplayAccount}>
              <IdleTimeoutWarning />
              <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <div className="App">
                <Helmet>
                  <title>Mergon One Manufacturing Intelligence</title>
                  <meta name="description" content="Advanced manufacturing intelligence platform" />
                </Helmet>

                <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
                  <Routes>
                    {/* ============= PUBLIC ROUTES ============= */}
                    <Route 
                      path="/" 
                      element={user ? (hasPermission(user, 'VIEW_DASHBOARD') ? <Navigate to="/dashboard" replace /> : <Navigate to="/profile" replace />) : (<LandingPage />)} 
                    />
                    <Route 
                      path="/login" 
                      element={<Login setUser={updateUser} />} 
                    />

                    {/* ============= AUTHENTICATED ROUTES ============= */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_DASHBOARD">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <Dashboard user={user} setUser={updateUser} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute user={user}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <Profile user={user} setUser={updateUser} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* User Performance Stats - any authenticated user can view their own stats */}
                    {/* Managers automatically see their direct reports based on role hierarchy */}
                    <Route 
                      path="/my-stats" 
                      element={
                        <ProtectedRoute user={user}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <UserStats user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= RBAC & ADMIN ============= */}
                    <Route 
                      path="/users/*" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_USERS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <UserManagement />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/rbac/*" 
                      element={
                        <ProtectedRoute user={user} permissions={["MANAGE_ROLES", "MANAGE_PERMISSIONS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <RBACManagement />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= COMPLIANCE REPORTING (FDA 21 CFR Part 11) ============= */}
                    <Route
                      path="/compliance/*"
                      element={
                        <ProtectedRoute user={user} permissions={["VIEW_COMPLIANCE_REPORTS", "EXPORT_COMPLIANCE_DATA"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ComplianceReporting user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ============= ASSET MANAGEMENT (CONSOLIDATED) ============= */}
                    <Route 
                      path="/assets" 
                      element={
                        <ProtectedRoute user={user} permissions={["VIEW_MACHINES", "VIEW_MOLDS", "VIEW_PARTS", "MANAGE_MACHINES", "MANAGE_MOLDS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AssetManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/assets/:tab" 
                      element={
                        <ProtectedRoute user={user} permissions={["VIEW_MACHINES", "VIEW_MOLDS", "VIEW_PARTS", "MANAGE_MACHINES", "MANAGE_MOLDS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AssetManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= ASSET TREE (OT INFRASTRUCTURE) ============= */}
                    <Route
                      path="/asset-tree"
                      element={
                        <ProtectedRoute user={user} permission="VIEW_ASSET_TREE">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AssetTree user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ============= DOCUMENT CONTROL ============= */}
                    <Route
                      path="/document-control"
                      element={
                        <ProtectedRoute user={user} permission="VIEW_DOCUMENTS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <DocumentControl user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ============= MANUFACTURING CORE (LEGACY - redirect to Assets) ============= */}
                    <Route 
                      path="/machines/*" 
                      element={<Navigate to="/assets/machines" replace />}
                    />

                    <Route 
                      path="/parameters" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_PARAMETERS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ParameterManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/molds/*" 
                      element={<Navigate to="/assets/molds" replace />}
                    />

                    <Route 
                      path="/parts/*" 
                      element={<Navigate to="/assets/parts" replace />}
                    />

                    {/* ============= REPORTING ============= */}
                    <Route 
                      path="/shift-report" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SHIFT_REPORTS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ShiftReport />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route
                      path="/timeline"
                      element={
                        <ProtectedRoute user={user} permission="VIEW_MACHINES">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <Timeline user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/issue-configuration/*"
                      element={
                        <ProtectedRoute user={user} permissions={["MANAGE_PROBLEMS", "MANAGE_RESOLUTION_OPTIONS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ResolutionManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Redirects from old routes */}
                    <Route path="/problems/*" element={<Navigate to="/issue-configuration" replace />} />
                    <Route path="/resolution-options/*" element={<Navigate to="/issue-configuration" replace />} />

                    <Route
                      path="/diagnostic-trees"
                      element={
                        <ProtectedRoute user={user} permission="VIEW_DIAGNOSTIC_TREES">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <DiagnosticTreeManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ============= AI & AUTOMATION ============= */}
                    <Route 
                      path="/ai-chat" 
                      element={
                        <ProtectedRoute user={user} permission="ACCESS_AI_CHAT">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AIChat user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= KAIZEN MANAGEMENT ============= */}
                    <Route 
                      path="/kaizen" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_KAIZEN">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <KaizenList user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/kaizen/submit" 
                      element={
                        <ProtectedRoute user={user} permission="CREATE_KAIZEN">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <KaizenForm user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/kaizen/list" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_KAIZEN">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <KaizenList user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/kaizen/leaderboard" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_KAIZEN_LEADERBOARD">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <KaizenLeaderboard user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= 6S MANAGEMENT (COMPLETE) ============= */}
                    <Route 
                      path="/6s" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SIXS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <SixSManagement />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/6s/areas" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SIXS_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AreaList />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/6s/audit/:areaId" 
                      element={
                        <ProtectedRoute user={user} permission="CREATE_SIXS_AUDITS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <AuditForm />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/6s/analysis" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SIXS_ANALYTICS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <Analysis />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= SAFETY MANAGEMENT ============= */}
                    <Route 
                      path="/safety" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SAFETY">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <SafetyDashboard user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/safety/incidents" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SAFETY_INCIDENTS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <SafetyIncidentList user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/safety/incidents/new" 
                      element={<IncidentForm user={user} />} 
                    />
                    <Route 
                      path="/safety/incidents/:id" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_SAFETY_INCIDENTS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <SafetyIncidentDetails currentUser={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />


                    {/* ============= PREVENTIVE MAINTENANCE (COMPLETE) ============= */}
                    <Route 
                      path="/pm" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_PM_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMDashboard />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pm/areas" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_PM_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMAreaList />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pm/areas/new" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_PM_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMAreaForm />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pm/areas/edit/:id" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_PM_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMAreaEditWrapper />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pm/tasks/:areaId" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_PM_AREAS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMTaskExecutionWrapper />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pm/analytics" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_PM_ANALYTICS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PMAnalytics />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= SIMPLIFIED CHANGEOVER MANAGEMENT ============= */}
                    <Route 
                      path="/changeovers" 
                      element={
                        <ProtectedRoute 
                          user={user} 
                          permissions={['MANAGE_SMED', 'EXECUTE_SMED']}
                          requireAll={false}
                        >
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChangeoverList user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/changeovers/schedule" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_SMED">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ScheduleChangeover user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= CHANGEOVER TEMPLATES ============= */}
                    <Route 
                      path="/changeovers/templates" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_SMED">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChecklistTemplateList user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/changeovers/templates/new" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_SMED">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChecklistTemplateBuilder user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/changeovers/templates/:id" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_SMED">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChecklistTemplateBuilder user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/changeovers/templates/:id/edit" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_SMED">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChecklistTemplateBuilder user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= CHANGEOVER DASHBOARD (MAIN ENTRY POINT) ============= */}
                    <Route 
                      path="/smed" 
                      element={
                        <ProtectedRoute 
                          user={user} 
                          permissions={['MANAGE_SMED', 'EXECUTE_SMED']}
                          requireAll={false}
                        >
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <ChangeoverDashboard user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />



                    {/* ============= LEAVE MANAGEMENT ============= */}
                    {/* Main leave calendar - any authenticated user can see their own leaves + peers */}
                    <Route 
                      path="/leave/*" 
                      element={
                        <ProtectedRoute user={user}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <LeaveManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* Legacy routes - redirect to /leave */}
                    <Route 
                      path="/leave/personal" 
                      element={<Navigate to="/leave" replace />}
                    />
                    <Route 
                      path="/my-leave" 
                      element={<Navigate to="/leave" replace />}
                    />

                    {/* ============= HR MANAGEMENT ============= */}
                    <Route 
                      path="/hr/settings" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_HR">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HRSettings user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/hr/employees" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_HR">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HREmployeeProfiles user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/hr/hierarchy" 
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_HR">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HRTeamHierarchy user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= PURCHASE REQUEST MANAGEMENT ============= */}
                    <Route 
                      path="/purchase-requests" 
                      element={
                        <ProtectedRoute user={user} permissions={["CREATE_PURCHASE_REQUEST", "MANAGE_PURCHASE_REQUESTS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PurchaseRequestManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= QUALITY MANAGEMENT ============= */}
                    <Route 
                      path="/quality" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_QUALITY_DASHBOARD">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <QualityDashboard user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    <Route 
                      path="/quality/analytics" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_QUALITY_DASHBOARD">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <QualityAnalytics user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= HELP CENTER ============= */}
                    {/* Public help center - any authenticated user */}
                    <Route 
                      path="/help" 
                      element={
                        <ProtectedRoute user={user}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HelpCenter />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/help/:slug" 
                      element={
                        <ProtectedRoute user={user}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HelpArticle />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    {/* Admin help article management */}
                    <Route
                      path="/admin/help"
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_HELP_ARTICLES">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HelpManagement />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin help request reason management */}
                    <Route
                      path="/admin/help-request-reasons"
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_HELP_REQUEST_REASONS">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <HelpRequestReasonSettings user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ============= TRAINING MANAGEMENT ============= */}
                    {/* User training dashboard - view assigned trainings */}
                    <Route 
                      path="/training" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_TRAINING">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingDashboard user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    {/* View training content and take quiz */}
                    <Route 
                      path="/training/:assignmentId" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_TRAINING">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingDetailsWrapper user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />
                    {/* Admin: Create new training (must be before :tab wildcard) */}
                    <Route
                      path="/training/manage/new"
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_TRAINING">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingBuilder user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin: Edit existing training (must be before :tab wildcard) */}
                    <Route
                      path="/training/manage/:id/edit"
                      element={
                        <ProtectedRoute user={user} permission="MANAGE_TRAINING">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingBuilderWrapper user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Training Management - consolidated tabbed page (trainings, assignments, reports) */}
                    <Route
                      path="/training/manage"
                      element={
                        <ProtectedRoute user={user} permissions={["MANAGE_TRAINING", "ASSIGN_TRAINING", "VIEW_TRAINING_REPORTS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingManagementPage user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/training/manage/:tab"
                      element={
                        <ProtectedRoute user={user} permissions={["MANAGE_TRAINING", "ASSIGN_TRAINING", "VIEW_TRAINING_REPORTS"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingManagementPage user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Assignments sub-tabs within the consolidated page */}
                    <Route
                      path="/training/manage/assignments/:subtab"
                      element={
                        <ProtectedRoute user={user} permissions={["ASSIGN_TRAINING", "MANAGE_TRAINING"]} requireAll={false}>
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <TrainingManagementPage user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy redirects */}
                    <Route
                      path="/training/assignments"
                      element={<Navigate to="/training/manage/assignments" replace />}
                    />
                    <Route
                      path="/training/assignments/:tab"
                      element={<Navigate to="/training/manage/assignments" replace />}
                    />
                    <Route
                      path="/training/reports"
                      element={<Navigate to="/training/manage/reports" replace />}
                    />
                    <Route
                      path="/training/role-config"
                      element={<Navigate to="/training/manage/assignments/role-config" replace />}
                    />
                    <Route
                      path="/training/onboarding"
                      element={<Navigate to="/training/manage/assignments/onboarding" replace />}
                    />

                    {/* ============= PLC MONITORING ============= */}
                    <Route 
                      path="/plc-monitoring" 
                      element={
                        <ProtectedRoute user={user} permission="VIEW_PLC_CONFIG">
                          <AuthenticatedLayout user={user} setUser={updateUser} handleLogout={handleLogout}>
                            <PLCManagement user={user} />
                          </AuthenticatedLayout>
                        </ProtectedRoute>
                      } 
                    />

                    {/* ============= ERROR ROUTES ============= */}
                    <Route 
                      path="/unauthorized" 
                      element={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center p-8">
                            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
                            <p className="text-gray-600 mb-6">You don't have permission to access this resource.</p>
                            <button 
                              onClick={() => window.history.back()}
                              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                            >
                              Go Back
                            </button>
                          </div>
                        </div>
                      } 
                    />

                    {/* 404 Route */}
                    <Route 
                      path="*" 
                      element={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center p-8">
                            <h1 className="text-2xl font-bold text-gray-800 mb-4">Page Not Found</h1>
                            <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                            <button 
                              onClick={() => window.location.href = user ? '/dashboard' : '/'}
                              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                            >
                              {user ? 'Go to Dashboard' : 'Go Home'}
                            </button>
                          </div>
                        </div>
                      } 
                    />
                  </Routes>
                </Suspense>
              </div>
              </Router>
            </ActivityTrackingProvider>
          </NotificationProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;